/**
 * TzamunAI branding primitives for the AI Diagnosis Copilot surfaces.
 *
 * The AI assistant is powered by the TzamunAI gateway, so its entry buttons,
 * modal header and footer carry the TzamunAI mark + brand teal. Logos live in
 * /public/brand (downscaled wordmarks). The stored brand colour is the teal
 * from the TzamunAI site (#00BFA5 → #00D4AA gradient).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

export const TZAMUN_AI_TEAL = '#00BFA5';
export const TZAMUN_AI_TEAL_2 = '#00D4AA';

// Inline gradient used for branded primary (text-only) buttons.
export const TZAMUN_AI_GRADIENT = {
  backgroundImage: `linear-gradient(135deg, ${TZAMUN_AI_TEAL} 0%, ${TZAMUN_AI_TEAL_2} 100%)`,
};

// Deep navy→teal brand backdrop. The full-colour logo + AI mark (white text,
// teal/blue/green glyphs) are washed out on a flat teal fill, so anything that
// sits BEHIND the logo/mark uses this dark base instead — teal stays for hover.
export const TZAMUN_AI_DARK_1 = '#0A2540';
export const TZAMUN_AI_DARK_2 = '#0E3A3A';
export const TZAMUN_AI_DARK_GRADIENT = {
  backgroundImage: `linear-gradient(135deg, ${TZAMUN_AI_DARK_1} 0%, ${TZAMUN_AI_DARK_2} 100%)`,
};

// Subtle teal-tinted wash for the whole AI popup panel. Semi-transparent so it
// composites over the theme surface (works in light + dark) — brands the popup
// without hurting form-field contrast.
export const TZAMUN_AI_PANEL_STYLE = {
  backgroundImage: `linear-gradient(160deg, rgba(0,191,165,0.16) 0%, rgba(0,212,170,0.06) 28%, rgba(0,191,165,0) 60%)`,
};

const LOGO = {
  color: '/brand/tzamun-ai-logo-color.png',
  white: '/brand/tzamun-ai-logo-white.png',
};

// AI-only mark cropped from the wordmark — used as a compact icon on buttons.
const MARK = {
  color: '/brand/tzamun-ai-icon-color.png',
  white: '/brand/tzamun-ai-icon-white.png',
};

/**
 * Full TzamunAI wordmark logo. variant 'color' (on light) or 'white' (on the
 * teal strip / dark surfaces). Sized by className height (aspect ratio ~4:1).
 */
export function TzamunAiLogo({ variant = 'color', className = 'h-5' }) {
  return (
    <img
      src={LOGO[variant] || LOGO.color}
      alt="TzamunAI"
      className={`${className} w-auto select-none`}
      draggable={false}
    />
  );
}

/**
 * The "AI" mark only (no wordmark) — the compact icon for entry buttons.
 */
export function TzamunAiMark({ variant = 'white', className = 'h-5', style }) {
  return (
    <img
      src={MARK[variant] || MARK.white}
      alt="TzamunAI"
      // `style` lets a caller pin an exact pixel box (ui/TzamunAiIcon uses it to
      // meet the react-icons `size` contract). Without one, height comes from
      // className and the width follows the aspect ratio.
      className={`${className} ${style?.width ? '' : 'w-auto'} select-none`}
      style={style}
      draggable={false}
    />
  );
}

/**
 * "Powered by TzamunAI" row: the full wordmark followed by the label —
 * shown in the assistant footer.
 */
export function PoweredByTzamunAi({ className = '' }) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center justify-center gap-2 text-xs text-content-muted ${className}`}
    >
      <TzamunAiLogo variant="color" className="h-4" />
      <span>{t('aiPoweredBy')}</span>
    </div>
  );
}
